import { Currency } from '@kodiak-finance/sdk-core'
import CurrencyLogo from 'components/CurrencyLogo'
import { SupportedChainId } from 'config/chains'
import { COMMON_BASES } from 'constants/routing'
import { PROMOTE_TOKENS_BY_CHAIN } from 'constants/tokens'
import { useTokenInfoFromActiveList } from 'hooks/useTokenInfoFromActiveList'
import { Text } from 'rebass'
import styled from 'styled-components'
import { currencyId } from 'utils/currencyId'

const BaseWrapper = styled.button<{ $disable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px 6px 8px;
  border: 1px solid ${({ theme }) => theme.cardBorderPrimary};
  border-radius: 6px;
  background-color: ${({ theme, $disable }) => ($disable ? theme.hoverDefault : theme.cardTertiary)};
  color: ${({ theme, $disable }) => ($disable ? theme.textTertiary : theme.textPrimary)};
  filter: ${({ $disable }) => $disable && 'grayscale(1)'};
  cursor: ${({ $disable }) => ($disable ? 'default' : 'pointer')};

  :hover {
    background-color: ${({ theme, $disable }) => !$disable && theme.hoverState};
  }
`

const CommonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
`

const CurrencyText = styled(Text)`
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
`

function deduplicateCurrencies(currencies: Currency[]): Currency[] {
  const seen = new Set<string>()
  const deduplicated: Currency[] = []

  for (const currency of currencies) {
    // Create unique key: chainId-address (or chainId-NATIVE for native currencies)
    const key = currency.isNative
      ? `${currency.chainId}-NATIVE`
      : `${currency.chainId}-${currency.wrapped.address.toLowerCase()}`

    if (!seen.has(key)) {
      seen.add(key)
      deduplicated.push(currency)
    }
  }

  return deduplicated
}

export default function CommonBases({
  chainId,
  onSelect,
  selectedCurrency,
  searchQuery,
  isAddressSearch,
  disableNonToken = false,
  preferredCurrencies,
}: {
  chainId?: number
  selectedCurrency?: Currency | null
  onSelect: (currency: Currency) => void
  searchQuery: string
  isAddressSearch: string | false
  disableNonToken?: boolean
  preferredCurrencies?: Currency[]
}) {
  const basesRaw =
    typeof chainId !== 'undefined'
      ? [
          // Add preferred currencies first
          ...(preferredCurrencies ?? []),
          // Then add common bases
          ...(COMMON_BASES[chainId] ?? []),
          ...(PROMOTE_TOKENS_BY_CHAIN[chainId as SupportedChainId]
            ? ([PROMOTE_TOKENS_BY_CHAIN[chainId as SupportedChainId]] as Currency[])
            : []),
        ]
      : []

  // Deduplicate to handle cases where preferredCurrencies might already be in COMMON_BASES
  let bases = deduplicateCurrencies(basesRaw)

  if (disableNonToken) {
    bases = bases.filter((currency) => currency.isToken)
  }

  return bases.length > 0 ? (
    <CommonRow>
      {bases.map((currency: Currency) => {
        const isSelected = selectedCurrency?.equals(currency)

        return (
          <BaseWrapper
            type="button"
            onClick={() => !isSelected && onSelect(currency)}
            $disable={isSelected}
            disabled={isSelected}
            key={currencyId(currency)}
          >
            <CurrencyLogoFromList currency={currency} />
            <CurrencyText>{currency.symbol}</CurrencyText>
          </BaseWrapper>
        )
      })}
    </CommonRow>
  ) : null
}

/** helper component to retrieve a base currency from the active token lists */
function CurrencyLogoFromList({ currency }: { currency: Currency }) {
  const token = useTokenInfoFromActiveList(currency)
  return <CurrencyLogo currency={token} size="24px" />
}
